import React, { useState } from 'react';
import { Trash2, X } from 'lucide-react';
import api from '../../services/api';

// Manual adjustments for one employee's month, before approval: override a
// calculated earning/deduction, or add a one-off line (bonus, arrears,
// recovery). Every change needs a reason and is recorded in the system log.

const inputStyle = {
    width: '100%', padding: '0.5rem', borderRadius: '0.5rem',
    border: '1px solid var(--input-border, var(--border))', background: 'var(--input-bg, var(--surface))', color: 'var(--text-main)',
};
const label = { display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.3rem', color: 'var(--text-secondary)' };

const EMPTY = { kind: 'EARNING', mode: 'ADD', component: '', amount: '', reason: '' };

const PayrollAdjustmentModal = ({ row, month, year, formatCurrency, onClose, onRowUpdated }) => {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [current, setCurrent] = useState(row);

    const lines = form.kind === 'EARNING' ? current.earningsBreakdown : current.taxBreakdown;
    const overridable = (lines || []).filter(l => !l.oneOff);

    const update = (patch) => setForm(f => {
        const next = { ...f, ...patch };
        // Switching to "override" preselects the first line and its amount.
        if ((patch.mode === 'OVERRIDE' || (patch.kind && next.mode === 'OVERRIDE'))) {
            const src = next.kind === 'EARNING' ? current.earningsBreakdown : current.taxBreakdown;
            const first = (src || []).find(l => !l.oneOff);
            next.component = first?.name || '';
            next.amount = first ? String(first.amount) : '';
        }
        if (patch.mode === 'ADD') next.component = '';
        return next;
    });

    const pickLine = (name) => {
        const line = overridable.find(l => l.name === name);
        setForm(f => ({ ...f, component: name, amount: line ? String(line.amount) : f.amount }));
    };

    const applyResult = (data) => {
        if (data.row) {
            setCurrent(data.row);
            onRowUpdated(data.row);
        }
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const { data } = await api.put('/payroll/adjustments', { ...form, userId: row.user.id, month, year });
            applyResult(data);
            setForm(EMPTY);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save adjustment');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Remove this adjustment? The line goes back to its calculated amount.')) return;
        setError(null);
        try {
            const { data } = await api.delete(`/payroll/adjustments/${id}`);
            applyResult(data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to remove adjustment');
        }
    };

    const Lines = ({ title, items, total }) => (
        <div>
            <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.35rem' }}>{title}</div>
            {(items || []).map((l, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '0.2rem 0', color: l.adjusted ? '#b45309' : 'inherit' }}>
                    <span>{l.name}{l.adjusted ? ' *' : ''}</span><span>{formatCurrency(l.amount)}</span>
                </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', fontWeight: 600, borderTop: '1px solid var(--border)', marginTop: '0.3rem', paddingTop: '0.3rem' }}>
                <span>Total</span><span>{formatCurrency(total)}</span>
            </div>
        </div>
    );

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }}>
            <div style={{ background: 'var(--surface)', borderRadius: '1rem', width: '100%', maxWidth: '44rem', maxHeight: '92vh', overflowY: 'auto', padding: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                    <div>
                        <h2 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0 }}>Adjust salary: {row.user.name}</h2>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                            {new Date(year, month - 1).toLocaleString('default', { month: 'long' })} {year} · applies to this month only
                        </div>
                    </div>
                    <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={20} /></button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', background: 'var(--bg-secondary)', borderRadius: '0.75rem', padding: '1rem', marginBottom: '1rem' }}>
                    <Lines title="Earnings" items={current.earningsBreakdown} total={current.earnedGross} />
                    <Lines title="Deductions" items={current.taxBreakdown} total={current.taxDeductions} />
                    <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                        <span>Net pay</span><span>{formatCurrency(current.netPay)}</span>
                    </div>
                </div>

                {current.adjustments?.length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.4rem' }}>Adjustments this month</div>
                        {current.adjustments.map(a => (
                            <div key={a.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.5rem 0.75rem', border: '1px solid var(--border)', borderRadius: '0.5rem', marginBottom: '0.4rem', fontSize: '0.85rem' }}>
                                <div style={{ flex: 1 }}>
                                    <strong>{a.component}</strong>{' '}
                                    <span style={{ color: 'var(--text-muted)' }}>
                                        {a.kind === 'EARNING' ? 'earning' : 'deduction'} · {a.mode === 'ADD' ? 'one-off' : 'override'} · {formatCurrency(a.amount)}
                                    </span>
                                    <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>{a.reason}</div>
                                </div>
                                <button onClick={() => remove(a.id)} title="Remove adjustment" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}><Trash2 size={16} /></button>
                            </div>
                        ))}
                    </div>
                )}

                <form onSubmit={save} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                    <div>
                        <label style={label} htmlFor="adj-kind">Type</label>
                        <select id="adj-kind" value={form.kind} onChange={e => update({ kind: e.target.value })} style={inputStyle}>
                            <option value="EARNING">Earning</option>
                            <option value="DEDUCTION">Deduction</option>
                        </select>
                    </div>
                    <div>
                        <label style={label} htmlFor="adj-mode">Change</label>
                        <select id="adj-mode" value={form.mode} onChange={e => update({ mode: e.target.value })} style={inputStyle}>
                            <option value="ADD">Add a one-off line</option>
                            <option value="OVERRIDE" disabled={overridable.length === 0}>Change a calculated line</option>
                        </select>
                    </div>
                    <div>
                        <label style={label} htmlFor="adj-component">{form.mode === 'OVERRIDE' ? 'Line' : 'Label'}</label>
                        {form.mode === 'OVERRIDE' ? (
                            <select id="adj-component" value={form.component} onChange={e => pickLine(e.target.value)} style={inputStyle}>
                                {overridable.map(l => <option key={l.name} value={l.name}>{l.name}</option>)}
                            </select>
                        ) : (
                            <input id="adj-component" required value={form.component} onChange={e => setForm({ ...form, component: e.target.value })}
                                placeholder={form.kind === 'EARNING' ? 'e.g. Performance bonus, Arrears' : 'e.g. Salary advance recovery'} style={inputStyle} />
                        )}
                    </div>
                    <div>
                        <label style={label} htmlFor="adj-amount">{form.mode === 'OVERRIDE' ? 'New amount for this month' : 'Amount'}</label>
                        <input id="adj-amount" required type="number" min="0" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} style={inputStyle} />
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                        <label style={label} htmlFor="adj-reason">Reason (shown on the payslip and in the audit log)</label>
                        <input id="adj-reason" required minLength={3} value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} style={inputStyle} />
                    </div>
                    {error && <div style={{ gridColumn: '1 / -1', color: '#dc2626', fontSize: '0.85rem' }}>{error}</div>}
                    <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        <button type="button" onClick={onClose} className="btn btn-secondary">Done</button>
                        <button type="submit" disabled={saving} className="btn btn-primary">{saving ? 'Saving…' : 'Save adjustment'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PayrollAdjustmentModal;
